/**
 * O editor manual do exame e do relatório de risco (E171.F3, GUS-145 e GUS-46; ADR-0122 §2.4–§2.7).
 * Espelha `apps/api/types/edicoes_do_laudo.py` (o lote de "Salvar nova versão") e
 * `apps/api/types/rascunhos_das_edicoes.py` (o rascunho de cada trecho).
 *
 * - `GET /casos/:id/correcao/trechos?saida=exame|riscos`: a composição atual da saída.
 * - `POST /casos/:id/correcao/versoes`: as edições da saída, todas ou nenhuma; a prévia é refeita.
 * - `GET /casos/:id/correcao/rascunhos?saida=…`, `PUT` e `DELETE …/rascunhos/:saida/:trecho`: o que a
 *   pessoa digitou e ainda não salvou, só dela.
 *
 * A tela não decide nada: o inventário, a guarda e a situação de cada rascunho vêm do servidor.
 */
import type { AxiosInstance } from 'axios';

import { ptBR } from '@/i18n/pt-BR';

import {
  ACOES_COM_TEXTO,
  CorrecaoDoRascunhoError,
  FUNDAMENTO_MINIMO,
  motivoDoErro,
  type AcaoDaEdicao,
  type EdicaoDoLaudoData,
  type SaidaDaEdicao,
  type TrechoNaTelaData,
  type TrechosDoLaudoData,
} from './correcaoDoRascunho';

export type SituacaoDoRascunho = 'vigente' | 'outra_versao' | 'conflito';

export interface RascunhoNaTelaData {
  acao: AcaoDaEdicao;
  texto: string;
  fundamento: string;
  trecho_id: string;
  avaliacao_id: string;
  anterior_id: string | null;
  atualizado_em: string;
  situacao: SituacaoDoRascunho;
}

export interface RascunhosDaSaidaData {
  case_id: string;
  saida: SaidaDaEdicao;
  avaliacao_id: string | null;
  rascunhos: RascunhoNaTelaData[];
}

export interface PedidoDeRascunhoData {
  acao: AcaoDaEdicao;
  texto: string;
  fundamento: string;
  avaliacao_id: string;
  anterior_id?: string | null;
}

export interface EdicaoDaVersaoData {
  acao: AcaoDaEdicao;
  texto?: string | null;
  trecho_id: string;
  fundamento: string;
  anterior_id?: string | null;
  base_sha256?: string | null;
}

export interface PedidoDeVersaoData {
  saida: SaidaDaEdicao;
  avaliacao_id: string;
  edicoes: EdicaoDaVersaoData[];
}

export interface VersaoSalvaData {
  case_id: string;
  saida: SaidaDaEdicao;
  avaliacao_id: string;
  edicoes_sha256: string | null;
  edicoes: EdicaoDoLaudoData[];
}

/** O que está no editor de um trecho: a ação, o texto e o fundamento. */
export interface ConteudoDoEditor {
  acao: AcaoDaEdicao;
  texto: string;
  fundamento: string;
}

async function chamar<T>(pedido: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await pedido()).data;
  } catch (err) {
    throw new CorrecaoDoRascunhoError(motivoDoErro(err));
  }
}

const base = (caseId: string) => `/casos/${caseId}/correcao`;
const doTrecho = (caseId: string, saida: SaidaDaEdicao, trechoId: string) =>
  `${base(caseId)}/rascunhos/${saida}/${encodeURIComponent(trechoId)}`;

export function lerTrechosDaSaida(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDaEdicao,
): Promise<TrechosDoLaudoData> {
  return chamar(() =>
    client.get<TrechosDoLaudoData>(`${base(caseId)}/trechos`, { params: { saida } }),
  );
}

export function lerRascunhos(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDaEdicao,
): Promise<RascunhosDaSaidaData> {
  return chamar(() =>
    client.get<RascunhosDaSaidaData>(`${base(caseId)}/rascunhos`, { params: { saida } }),
  );
}

export function guardarRascunho(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDaEdicao,
  trechoId: string,
  pedido: PedidoDeRascunhoData,
): Promise<RascunhoNaTelaData> {
  return chamar(() => client.put<RascunhoNaTelaData>(doTrecho(caseId, saida, trechoId), pedido));
}

export async function descartarRascunho(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDaEdicao,
  trechoId: string,
): Promise<void> {
  await chamar(() => client.delete<unknown>(doTrecho(caseId, saida, trechoId)));
}

export function salvarVersao(
  client: AxiosInstance,
  caseId: string,
  pedido: PedidoDeVersaoData,
): Promise<VersaoSalvaData> {
  return chamar(() => client.post<VersaoSalvaData>(`${base(caseId)}/versoes`, pedido));
}

/** O que falta para salvar a versão; `null` quando o conteúdo está completo. */
/** Os tetos do servidor (`types/edicoes_do_laudo.py`): o texto (com as marcas) e o fundamento. */
export const TEXTO_MAXIMO = 2000;
export const FUNDAMENTO_MAXIMO = 500;

export function faltaParaSalvar(
  conteudo: ConteudoDoEditor,
): 'fundamento' | 'texto' | 'longo' | null {
  if (conteudo.fundamento.trim().length < FUNDAMENTO_MINIMO) return 'fundamento';
  if (!ACOES_COM_TEXTO.has(conteudo.acao)) return null;
  if (conteudo.texto.trim() === '') return 'texto';
  return conteudo.texto.length > TEXTO_MAXIMO ? 'longo' : null;
}

/** A edição do lote com o que a tela viu do trecho (a guarda otimista do servidor). */
export function edicaoDaVersao(
  trecho: TrechoNaTelaData,
  conteudo: ConteudoDoEditor,
): EdicaoDaVersaoData {
  return {
    acao: conteudo.acao,
    texto: ACOES_COM_TEXTO.has(conteudo.acao) ? conteudo.texto.trim() : null,
    trecho_id: trecho.id,
    fundamento: conteudo.fundamento.trim(),
    anterior_id: trecho.vigente?.id ?? null,
    base_sha256: trecho.base_sha256,
  };
}

const t = ptBR.editorManual;

/** E184.F2: no relatório de risco, só o campo do texto do risco tira o risco inteiro. */
export function ehORisco(trecho: TrechoNaTelaData): boolean {
  return trecho.tipo === 'campo' && trecho.id.endsWith('.risco');
}

/** A ação faz sentido agora: não se tira o que não sai, nem se restaura o que não foi editado. */
export function cabe(trecho: TrechoNaTelaData, acao: AcaoDaEdicao): boolean {
  const vigente = trecho.vigente?.acao;
  if (acao === 'restaurar') return vigente !== undefined && vigente !== 'restaurar';
  if (acao === 'remover') {
    return trecho.no_laudo !== null && (trecho.tipo !== 'campo' || ehORisco(trecho));
  }
  return true;
}

export function rotuloDaAcao(trecho: TrechoNaTelaData, acao: AcaoDaEdicao): string {
  if (acao === 'remover' && trecho.tipo === 'nota') return t.acaoDaNota.remover;
  if (acao === 'remover' && ehORisco(trecho)) return t.acaoDoRisco.remover;
  return t.acao[acao];
}

/** E200.F3 (GUS-220 CA12): o vigente do trecho é a edição enviada — a versão foi salva, mesmo sem a
 * resposta ter voltado (a gravação que caiu por tempo ou rede). Saiu do editor antigo na E202.F3. */
export function versaoFoiSalva(trechos: TrechosDoLaudoData, enviada: EdicaoDaVersaoData): boolean {
  const vigente = trechos.trechos.find((x) => x.id === enviada.trecho_id)?.vigente;
  if (vigente == null) return false;
  return (
    vigente.acao === enviada.acao &&
    vigente.texto === (enviada.texto ?? null) &&
    vigente.fundamento === enviada.fundamento &&
    vigente.anterior_id === (enviada.anterior_id ?? null) &&
    vigente.base_sha256 === (enviada.base_sha256 ?? null)
  );
}

/** A espera depois da última tecla antes de guardar o rascunho. */
export const PAUSA_DO_RASCUNHO_MS = 1_200;
